import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosError } from 'axios'
import { type Envelope, isErrorEnvelope } from './envelope'
import { ErrorCode } from './error-codes'
import { ApmpApiError } from './errors'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v2'

/**
 * A best-effort write that must survive the page going away (`pagehide`): the browser's own
 * `fetch` with `keepalive`, carrying the same bearer token the client attaches. Nothing is
 * awaited and nothing is read back — the caller keeps its local copy regardless.
 */
export function keepaliveRequest(
  path: string,
  init: { method: string; body: unknown; headers?: Record<string, string> },
): void {
  if (typeof fetch === 'undefined') return
  const token = accessTokenGetter()
  try {
    void fetch(`${BASE_URL}${path}`, {
      method: init.method,
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
      body: JSON.stringify(init.body),
    })
  } catch {
    // Unloading; the local tier still has the work.
  }
}

/** Set by the auth store (Phase 02). Kept out of the store to avoid a circular import. */
let accessTokenGetter: () => string | null = () => null
let refreshHandler: (() => Promise<boolean>) | null = null
let unauthenticatedHandler: (() => void) | null = null

export function configureAuth(options: {
  getAccessToken: () => string | null
  refresh: () => Promise<boolean>
  onUnauthenticated: () => void
}) {
  accessTokenGetter = options.getAccessToken
  refreshHandler = options.refresh
  unauthenticatedHandler = options.onUnauthenticated
}

/**
 * For the one caller that cannot go through axios: a live stream (`./stream.ts`) reads its
 * body as it arrives, which axios cannot do in a browser. Same token, same single-flight refresh.
 */
export const streamAuth = {
  url: (path: string) => `${BASE_URL}${path}`,
  token: () => accessTokenGetter(),
  refresh: () => refreshOnce(),
}

function uuid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)
}

/** Extra per-request options this client understands. */
export interface ApmpRequestConfig extends AxiosRequestConfig {
  /**
   * A 401 on this request is an answer, not an expired session: do not sign the user out or
   * redirect them. Used by the session bootstrap, which asks "is anyone signed in?".
   */
  silentAuthFailure?: boolean
}

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

http.interceptors.request.use((config) => {
  const token = accessTokenGetter()
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers['X-Request-Id'] = uuid()
  /**
   * A file upload is `multipart/form-data`, and only the transport can write that header —
   * the boundary is generated with the body. The instance default is `application/json`, and
   * axios reads that default literally: it will serialise a `FormData` to JSON rather than
   * contradict a declared content type, so the server receives an empty body and parses zero
   * files. Deleting it here, once, is what makes every upload work; call sites do not need to
   * remember the trick.
   */
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete?.('Content-Type')
  }
  return config
})

/**
 * Single-flight refresh: many concurrent 401s must trigger ONE refresh, not one each.
 * Without this, a dashboard that fires six queries on mount would burn six refresh tokens
 * and, with rotation enabled, invalidate its own session.
 */
let refreshInFlight: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  if (!refreshHandler) return false
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.response) {
      throw ApmpApiError.network()
    }

    const { status, data, headers, config } = error.response
    const correlationId = (headers?.['x-correlation-id'] as string) || undefined
    const retryAfterRaw = headers?.['retry-after'] as string | undefined
    const retryAfter = retryAfterRaw ? Number(retryAfterRaw) : undefined

    // One retry, once, per request — and never for the refresh call itself.
    const original = config as ApmpRequestConfig & { _apmpRetried?: boolean }
    // Never refresh in response to an auth endpoint's own 401. Refreshing after a failed
    // login would be pointless; refreshing after a failed refresh would loop.
    //
    // The session bootstrap is deliberately NOT on that list: on a fresh page load the store
    // holds no access token and the `HttpOnly` refresh cookie is the only credential, so its
    // 401 is exactly the case a refresh is for.
    const url = original?.url ?? ''
    const isAuthEndpoint =
      url.includes('/auth/token/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/otp/')
    if (status === 401 && !original?._apmpRetried && !isAuthEndpoint) {
      original._apmpRetried = true
      if (await refreshOnce()) {
        return http.request(original)
      }
      // `silentAuthFailure` marks a call whose 401 is an ordinary answer rather than an
      // expired session — the bootstrap asking "is anyone signed in?". Treating that as a
      // session expiry sent every anonymous visitor to the login page with `next` pointing
      // at wherever the router happened to be, which was nowhere yet.
      if (!(original as ApmpRequestConfig)?.silentAuthFailure) {
        unauthenticatedHandler?.()
      }
    }

    if (isErrorEnvelope(data)) {
      throw ApmpApiError.fromEnvelope(data, status, { correlationId, retryAfter })
    }

    throw new ApmpApiError({
      message: 'Something went wrong. Please try again.',
      code: status >= 500 ? ErrorCode.SERVER_ERROR : ErrorCode.API_ERROR,
      status,
      correlationId,
      retryAfter,
    })
  },
)

/** Unwrap the envelope so callers get `data`, never the wrapper. */
async function unwrap<T>(promise: Promise<{ data: Envelope<T> }>): Promise<T> {
  const response = await promise
  const body = response.data
  if (isErrorEnvelope(body)) {
    throw ApmpApiError.fromEnvelope(body, 200)
  }
  return (body.data as T) ?? (undefined as T)
}

export const api = {
  get: <T>(url: string, config?: ApmpRequestConfig) => unwrap<T>(http.get(url, config)),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    unwrap<T>(http.post(url, body, config)),
  patch: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    unwrap<T>(http.patch(url, body, config)),
  put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    unwrap<T>(http.put(url, body, config)),
  delete: <T>(url: string, config?: AxiosRequestConfig) => unwrap<T>(http.delete(url, config)),

  /**
   * A mutation that must not be applied twice. The key is generated ONCE per logical attempt
   * and reused across transport retries — a timeout is a retry, a new user action is a new key
   * (api-description.md, Idempotency).
   */
  idempotentPost: <T>(url: string, body?: unknown, key: string = uuid()) =>
    unwrap<T>(http.post(url, body, { headers: { 'Idempotency-Key': key } })),

  /** Raw envelope, for the rare caller that needs `message`. */
  raw: <T>(url: string, config?: AxiosRequestConfig) =>
    http.get<Envelope<T>>(url, config).then((r) => r.data),
}
